import { Box } from '@mui/material';
import {
  type Connection,
  ConnectionLineType,
  ConnectionMode,
  type NodeChange,
  type NodeDimensionChange,
  useReactFlow,
} from '@xyflow/react';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import {
  CONNECTION_LINE_STYLE,
  CONNECTION_RADIUS,
  RECONNECT_RADIUS,
} from '@/components/diagrams/components/ConnectionHandles/constants';
import { HelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { REACT_FLOW_DELETE_KEYS } from '@/components/diagrams/FlowChart/constants';
import { FlowChart } from '@/components/diagrams/FlowChart/FlowChart';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import { useDiagramPlacementGhost } from '../../hooks/useDiagramPlacementGhost';
import {
  getConnectionEnds,
  getHandleSide,
  getPointerClientPosition,
} from '../../utils/diagramConnections';
import { STAGE_BAND_HEIGHT } from '../../utils/diagramLayout.config';
import {
  getDividerStageID,
  getStageDividerNodes,
  getStageHighlightNode,
  getStageIDAt,
  getTileCenterY,
  type StageBoundary,
} from '../../utils/diagramStages';
import { EDGE_TYPES, NODE_TYPES } from './constants';
import { isPlacementTool } from './diagramEditorTools';
import {
  DiagramQuickAddMenu,
  type DiagramQuickAddTarget,
} from './DiagramQuickAddMenu';

/** Renders the diagram in React Flow. `children` are canvas controls. */
export function DiagramFlowChart({
  children,
  minHeight = 420,
}: {
  children: ReactNode;
  minHeight?: number;
}) {
  const {
    diagram,
    reactFlowDiagram,
    locked,
    tool,
    placeNode,
    placeElement,
    openLabelEditor,
    canConnect,
    connect,
    reconnect,
    removeElements,
    moveStageBoundary,
    removeStageBoundary,
  } = useDiagramEditing();
  const { screenToFlowPosition } = useReactFlow();
  const [quickAdd, setQuickAdd] = useState<DiagramQuickAddTarget | null>(null);
  const placementGhost = useDiagramPlacementGhost(diagram, tool);
  const { ghost } = placementGhost;
  // The divider that the user drags, at its current height. The tiles stay
  // in place until the drop.
  const [dividerDrag, setDividerDrag] = useState<{
    stageID: string;
    y: number;
  } | null>(null);
  const boundaries = useMemo(
    (): StageBoundary[] =>
      reactFlowDiagram.stageBoundaries.map((boundary) =>
        boundary.stageID === dividerDrag?.stageID
          ? { ...boundary, y: dividerDrag.y }
          : boundary
      ),
    [reactFlowDiagram.stageBoundaries, dividerDrag]
  );
  // While a tile moves, the stage at its vertical center shows a tint. The
  // tile goes to that stage at the drop.
  const draggedTile = reactFlowDiagram.dragging
    ? reactFlowDiagram.nodes.find((node) => node.dragging)
    : undefined;
  const destinationStageID =
    draggedTile && boundaries.length
      ? getStageIDAt(
          diagram.content.stages,
          boundaries,
          getTileCenterY(draggedTile)
        )
      : undefined;
  // React Flow gives this handler to each line, so a stable function keeps
  // the lines from rendering again. A line that renders again under the
  // pointer loses the click that selects it.
  const onReconnect = useCallback(
    (edge: { id: string }, connection: Connection) => {
      const ends = getConnectionEnds(connection);
      if (ends) reconnect(edge.id, ends);
    },
    [reconnect]
  );

  // The React Flow diagram has the tiles with their Delete action. The tiles go to
  // React Flow without a copy, so a drag frame renders only the moved tile.
  // A locked canvas has no label actions, so its labels are not buttons.
  const nodes = useMemo(
    () => [
      ...reactFlowDiagram.nodes,
      ...getStageDividerNodes(
        boundaries,
        reactFlowDiagram.nodes,
        locked
          ? undefined
          : {
              onEdit: (id) => openLabelEditor({ kind: 'stage', id }),
              onDelete: removeStageBoundary,
            }
      ),
      ...(destinationStageID === undefined
        ? []
        : [
            getStageHighlightNode(
              boundaries,
              reactFlowDiagram.nodes,
              destinationStageID
            ),
          ]),
      // A node placement tool shows the node that a click adds.
      ...(ghost ? [ghost] : []),
    ],
    [
      reactFlowDiagram.nodes,
      boundaries,
      locked,
      openLabelEditor,
      removeStageBoundary,
      destinationStageID,
      ghost,
    ]
  );

  /**
   * Returns the height of a divider at node position `nodeY`. A divider can
   * go past the other dividers.
   */
  const getDividerY = (nodeY: number) => nodeY + STAGE_BAND_HEIGHT / 2;

  /**
   * Shows the dragged divider at the height of `change`. Returns false for a
   * change of another node.
   */
  const dragDivider = (change: NodeChange) => {
    if (change.type !== 'position') return false;
    const stageID = getDividerStageID(change.id);
    if (!stageID) return false;
    if (change.position)
      setDividerDrag({ stageID, y: getDividerY(change.position.y) });
    return true;
  };
  const edges = useMemo(
    () =>
      reactFlowDiagram.edges.map((edge) => ({
        ...edge,
        type: 'arrow' as const,
        // The user drags an end of the selected line to reconnect it.
        reconnectable: !locked && !!edge.selected,
      })),
    [reactFlowDiagram.edges, locked]
  );

  return (
    <Box display="flex" flex={1} minHeight={0}>
      <Box
        sx={{
          flex: 1,
          minHeight,
          // A plain white canvas, as in the Figma editor.
          backgroundColor: 'background.paper',
          // A placement tool adds an element where the user clicks.
          ...(isPlacementTool(tool) && {
            // Override the shared pane's grab and selection cursors.
            '&& .react-flow .react-flow__pane': { cursor: 'crosshair' },
          }),
          // Hide the tiles until they have positions.
          opacity: reactFlowDiagram.isPlaced ? 1 : 0,
        }}
      >
        <FlowChart
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          // `useReactFlowDiagram` fits the view when the tiles are placed.
          // React Flow's own first fit waits for the first node, so on an
          // empty canvas it moves the first tile that the user adds.
          fitView={false}
          nodesDraggable={!locked && !reactFlowDiagram.isArranging}
          // Each handle can start or end a line, so the user can connect any
          // side of a tile to any side of another tile.
          nodesConnectable={!locked}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={CONNECTION_RADIUS}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={CONNECTION_LINE_STYLE}
          isValidConnection={(connection) =>
            canConnect(connection.source, connection.target)
          }
          onConnect={(connection) => {
            const ends = getConnectionEnds(connection);
            if (ends) connect(ends);
          }}
          onConnectEnd={(event, state) => {
            // A line dropped on the empty canvas opens the quick-add menu.
            if (state.isValid || state.toNode || !state.fromNode) return;
            const side = getHandleSide(state.fromHandle?.id);
            if (!side) return;
            const client = getPointerClientPosition(event);
            if (!client) return;
            setQuickAdd({
              client,
              position: screenToFlowPosition(client),
              source: { nodeID: state.fromNode.id, side },
            });
          }}
          reconnectRadius={RECONNECT_RADIUS}
          onReconnect={onReconnect}
          deleteKeyCode={locked ? null : REACT_FLOW_DELETE_KEYS}
          // The diagram content decides which nodes and connections exist,
          // so React Flow does not remove them itself. One edit removes the
          // whole selection and the connections of each removed node.
          onBeforeDelete={async (selection) => {
            removeElements(
              ...selection.nodes.map((node) => node.id),
              ...selection.edges.map((edge) => edge.id)
            );
            return false;
          }}
          onMoveStart={() => setQuickAdd(null)}
          onMove={placementGhost.onMove}
          onPaneMouseMove={placementGhost.onPaneMouseMove}
          onPaneMouseLeave={placementGhost.onPaneMouseLeave}
          onPaneClick={(event) => {
            if (!isPlacementTool(tool)) return;
            placeElement(
              tool,
              placementGhost.getPlacementPosition(
                screenToFlowPosition({ x: event.clientX, y: event.clientY })
              )
            );
          }}
          onNodesChange={(allChanges) => {
            const changes = placementGhost.onNodesChange(
              allChanges.filter((change) => !dragDivider(change))
            );
            reactFlowDiagram.onNodesChange(changes);
            // The shared resizer reports `resizing: false` when the user
            // releases a resize handle.
            const resized = changes.find(
              (change): change is NodeDimensionChange =>
                change.type === 'dimensions' && change.resizing === false
            );
            const tile = reactFlowDiagram.nodes.find(
              (node) => node.id === resized?.id
            );
            if (tile) placeNode(tile, reactFlowDiagram.nodes);
          }}
          onEdgesChange={(changes) =>
            reactFlowDiagram.onEdgesChange(
              changes.filter((change) => change.type === 'select')
            )
          }
          onNodeDragStart={(_, node) => {
            if (!getDividerStageID(node.id)) reactFlowDiagram.setDragging(true);
          }}
          onNodeDragStop={(_, node, draggedNodes) => {
            const stageID = getDividerStageID(node.id);
            if (stageID) {
              // A drop saves the divider height as one edit. A click without
              // a move makes no edit. The drag state of this render can be
              // one move behind, so the height comes from the dropped node.
              const y = getDividerY(node.position.y);
              const saved = reactFlowDiagram.stageBoundaries.find(
                (item) => item.stageID === stageID
              );
              if (y !== saved?.y) moveStageBoundary(stageID, y);
              setDividerDrag(null);
              return;
            }
            reactFlowDiagram.setDragging(false);
            // Save the position from the React Flow diagram, which includes the
            // snap to the helper lines. The node from React Flow is its own
            // drag state. Each dragged tile goes to the stage at its center.
            placeNode(
              reactFlowDiagram.nodes.find((tile) => tile.id === node.id) ??
                node,
              reactFlowDiagram.nodes,
              new Set(draggedNodes.map((dragged) => dragged.id))
            );
          }}
        >
          {!locked && <HelperLines {...reactFlowDiagram.helperLines} />}
          {children}
        </FlowChart>
        {quickAdd && (
          <DiagramQuickAddMenu
            target={quickAdd}
            onClose={() => setQuickAdd(null)}
          />
        )}
      </Box>
    </Box>
  );
}
